import React, { useContext, useEffect, useState } from "react";
import { FormattedMessage } from "react-intl";
import "./BarChartDashboard.scss";
import Button from "core/buttons/button/Button";
import Icon from "core/icon/Icon";
import { differenceInCalendarDays, format, parseISO, subMonths } from "date-fns";
import { enUS, fr } from "date-fns/locale";
import { BarChartTable } from "components/dashboard-table/BarChartTable";
import {
  createHeaderItem,
  daysInMonth,
  formatTableContent,
  getDates,
  renderDate,
  renderTypography,
} from "utils/dashboardHelper";
import BarChartLegend from "components/dashboard-table/BarChartLegend";
import { useUserContext } from "api/user/UserProvider";
import { LANG, QUERY_STATUS } from "constants/constants";
import ScrollToTop from "core/buttons/scroll-buttons/ScrollToTop";
import InfoContext from "pages/InfoContext";
import { formatDateToYYYYMMDD } from "utils/formatters";
import Pagination from "core/pagination/Pagination";
import { useMissionContext } from "api/mission/MissionProvider";

export const BarChartDashboard = () => {
  const {
    dateFilter,
    clientsFilter,
    signedByFilter,
    chargedByFilter,
    missionTypeByFilter,
    dashboardBarChart,
    barChartStatus,
    getDashboardBarChart,
    exportBarChart,
    expandFilterIds,
    loadExportStatus,
  } = useContext(InfoContext);
  const { userScope, lang } = useUserContext();
  const [initialDateRange, setInitialDateRange] = useState([]);
  const [dates, setDates] = useState([]);
  const [firstDate, setFirstDate] = useState();
  const [lastDate, setLastDate] = useState();
  const [tableHeight, setTableHeight] = useState(0);
  const [widths, setWidths] = useState({
    maxWidth: 0,
    renderWidth: 0,
    monthContainerWidth: 0,
    pixelsPerDay: 2.25,
  });
  const [currentPage, setCurrentPage] = useState(1);
  const { setDisplayMission } = useMissionContext();

  useEffect(() => {
    if (!dateFilter || dateFilter === "") return;
    const _dates = getDates(parseISO(dateFilter));
    if (_dates) {
      setFirstDate(_dates[0]);
      setLastDate(_dates[_dates.length - 1]);
      setInitialDateRange(_dates);
      setDates(_dates);
    }
  }, [dateFilter]);

  useEffect(() => {
    if (firstDate && lastDate) {
      const maxWidth = differenceInCalendarDays(
        new Date(lastDate),
        new Date(firstDate)
      );
      const renderWidth = maxWidth * widths.pixelsPerDay;
      const datesPixelNum = dates?.reduce((acc, date) => {
        const daysNumberInMonth = daysInMonth(
          new Date(date).getMonth() + 1,
          new Date(date).getFullYear()
        );
        return acc + daysNumberInMonth;
      }, 0);
      const monthContainerWidth = datesPixelNum * widths.pixelsPerDay;
      setWidths({
        ...widths,
      });

      setWidths({
        ...widths,
        maxWidth,
        renderWidth,
        monthContainerWidth,
      });
    }
  }, [firstDate, lastDate, dateFilter]);

  const generateCurrentDayBar = (date) => {
    const monthStartDate = new Date(date.getFullYear(), date.getMonth(), 1);
    const leftPosition = getBarPosition(date, monthStartDate);

    return (
      <>
        <div
          className="head-bar"
          style={{ left: `${leftPosition - 9}px` }}
        ></div>
        <div
          className="vertical-bar"
          style={{
            height: `${tableHeight}px`,
            left: `${leftPosition}px`,
          }}
        ></div>
      </>
    );
  };

  const getBarPosition = (date, monthStartDate) => {
    const dayDiff = differenceInCalendarDays(date, monthStartDate);
    return dayDiff * widths.pixelsPerDay;
  };

  const header = [
    createHeaderItem("consultant", "bar_chart_cslt_str", "Consultant/ST", 210),
    createHeaderItem(
      "assignment-title",
      "mission_name",
      "Nom de la mission",
      350
    ),
    createHeaderItem("client", "client", "Client", 250),
    {
      ...createHeaderItem("date", "date", "", 783),
      label: {
        ...createHeaderItem("date", "date", "").label,
        value: (
          <>
            {dates?.map((date, index) => {
              const daysNumberInMonth = daysInMonth(
                date.getMonth() + 1,
                date.getFullYear()
              );
              const label = format(date, "MM/yy", {
                locale: lang === LANG.FR ? fr : enUS,
              });
              const filterDateObj =
                dateFilter && dateFilter !== "" ? parseISO(dateFilter) : null;
              const isCurrentDay =
                filterDateObj &&
                date.getMonth() === filterDateObj.getMonth() &&
                date.getFullYear() === filterDateObj.getFullYear();
              const key = format(date, "yyyy-MM");

              return (
                <React.Fragment key={`month-${key}`}>
                  <div
                    className={`bar-chart-date ${
                      isCurrentDay ? "current-day" : ""
                    }`}
                    style={{
                      width: `${daysNumberInMonth * widths.pixelsPerDay}px`,
                    }}
                  >
                    {isCurrentDay &&
                      filterDateObj &&
                      generateCurrentDayBar(filterDateObj)}
                    {label}
                  </div>
                </React.Fragment>
              );
            })}
          </>
        ),
      },
      width: 810,
    },
  ];

  const getCurrentPageContent = (page) => {
    if (
      barChartStatus !== QUERY_STATUS.LOADING &&
      page <= dashboardBarChart?.total_pages &&
      dateFilter &&
      dateFilter !== ""
    ) {
      setCurrentPage(page);
      getDashboardBarChart({
        page,
        date: formatDateToYYYYMMDD(dateFilter),
        signed_by: expandFilterIds(signedByFilter),
        charged_by: expandFilterIds(chargedByFilter),
        mission_type: missionTypeByFilter,
        client: clientsFilter.map((item) => item.value),
      });
    }
  };

  const titleStartDate =
    dateFilter && dateFilter !== ""
      ? subMonths(parseISO(dateFilter), 5)
      : firstDate;

  return (
    <div className="bar-chart-wrapper">
      <div className="bar-chart-top">
        <div className="bar-chart-title">
          <div className="bar-chart-title">
            {renderTypography("bar_chart", "Diagramme barre du ")}
            {renderDate({
              date: titleStartDate,
              lang,
            })}
            {renderTypography("to", "au")}
            {renderDate({
              date: lastDate,
              lang,
            })}
          </div>
        </div>
        <div className="bar-chart-export">
          <Button
            className="btn-outlined"
            size="small"
            color="white"
            disabled={loadExportStatus === QUERY_STATUS.LOADING}
            onClick={() => exportBarChart()}
          >
            <Icon icon="sheet" color="green" height={20} />
            <FormattedMessage
              id={"export_excel"}
              defaultMessage={"Exporter en Excel"}
            />
          </Button>
        </div>
      </div>
      <div className="bar-chart-table">
        <BarChartLegend />
        <div className="bar-chart-content">
          <ScrollToTop />
          {firstDate && lastDate && (
            <BarChartTable
              id={`missions-by-filters-${dashboardBarChart?.page}`}
              header={header}
              content={
                formatTableContent({
                  data: dashboardBarChart,
                  userScope,
                  setDisplayMission,
                }) || []
              }
              dates={initialDateRange}
              userDate={
                dateFilter && dateFilter !== "" ? parseISO(dateFilter) : null
              }
              firstDate={firstDate}
              lastDate={lastDate}
              tableWidth={"100%"}
              toolsManagerMission={true}
              lang={lang}
              setTableHeight={setTableHeight}
              allWidths={widths}
            />
          )}
          {dashboardBarChart?.total_items > 0 && (
            <div className="pagination">
              <Pagination
                total_pages={dashboardBarChart?.total_pages}
                getCurrentPageContent={getCurrentPageContent}
                page={currentPage}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BarChartDashboard;
